import { uiText } from '../../i18n';
import type { StudioDocument } from './studio-api';
import { displayTitle, priceParts, record, text, title } from './studio-content';
import { Fragment } from 'react';
import { StudioIcon } from './StudioIcon';

/** Разделы в том же порядке, что TSECS прототипа: он же порядок групп на странице при любой сортировке */
const sectionTitles: Record<string, string> = {
  circle: 'Кружки',
  photo: 'Фото',
  video: 'Видео',
  voice: 'Голос и музыка',
  text: 'Текст',
  biz: 'Для бизнеса',
};
/** Бейдж строки: сервер отдаёт ключ (§6.7), словами его называет клиент */
const badgeTitles: Record<string, string> = { hit: 'Хит', new: 'Новое' };
/** Подпись строки инструмента: «Нужно: …», как в toolRowHTML эталона */
function needLine(item: StudioDocument): string {
  const value = text(record(item.need).text) || text(item.need) || text(item.caption);
  if (!value) return '';
  /* Приставку ставит разметка, а не данные. Моки пока кладут её в сам текст — второй раз не пишем;
     на живом стенде поле приходит без приставки. */
  const label = uiText('Нужно');
  return value.startsWith(`${label}:`) ? value : `${label}: ${value}`;
}

/** Одна строка инструмента для каталога и поиска, как в шаблоне. */
export function StudioToolRows({
  items,
  onAction,
  grouped = false,
}: {
  items: StudioDocument[];
  onAction(action: unknown): void;
  grouped?: boolean;
}) {
  /* Группы идут в порядке разделов, а не в порядке строк: при «Дешевле» и «От А до Я» порядок групп у прототипа тот же (tlGroups) */
  const present = [...new Set(items.map(item => text(item.section)))];
  const order = Object.keys(sectionTitles);
  const sections = grouped ? [...order.filter(key => present.includes(key)), ...present.filter(key => !order.includes(key))] : [''];
  return (
    <div className="st-tll">
      {sections.map(section => {
        const rows = grouped ? items.filter(item => text(item.section) === section) : items;
        return (
          <Fragment key={section}>
            {grouped && section && (
              /* подзаголовок — прямой потомок .st-tll: на этом держится отступ между группами (bot-studio.css) */
              <div className="st-h st-trh" data-k={`h:${section}`}>
                <b>{uiText(sectionTitles[section] ?? section)}</b>
                <span className="st-cnt">{rows.length}</span>
              </div>
            )}
            <div className="st-grp">
              {rows.map(item => {
                const badge = text(item.badge);
                const price = priceParts(item.price);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="st-mr st-tr"
                    /* ключ строки — как у эталона (toolRowHTML): по названию, не по id */
                    data-k={`t:${title(item)}`}
                    onClick={() => onAction(item.action ?? { type: 'open_screen', entry: item.entry })}
                  >
                    <span className="ic">
                      <StudioIcon kind={text(item.icon) || text(item.section)} />
                    </span>
                    <span className="m">
                      <b>
                        <span className="tt">{displayTitle(item)}</span>
                        {badge && <i className="st-nw">{uiText(badgeTitles[badge] ?? badge)}</i>}
                      </b>
                      {/* эталон пишет «Нужно: …» (toolRowHTML); в данных приставки нет.
                          Пустое поле оставляем пустым: иначе в строке висит «Нужно:» без продолжения. */}
                      <span>{needLine(item)}</span>
                    </span>
                    {!!price.text && (
                      <span className="st-pr">
                        {price.text}
                        {!!price.unit && <span className="u">{price.unit}</span>}
                      </span>
                    )}
                    <span className="rt">
                      <StudioIcon kind="chev" />
                    </span>
                  </button>
                );
              })}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
